'use client'

import { useRouter } from 'next/navigation'
import React, { ReactNode, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useRecoilValue } from 'recoil'
import { z } from 'zod'

import { impressionIdState } from '@/components/Context/ImpressionProvider'
import { productQuantityDefaultValue, productQuantityInputKey } from '@/components/Form/Atoms/ProductQuantityInput'
import {
  productVariationDefaultValue,
  productVariationInputKey,
  productVariationScheme,
} from '@/components/Form/Atoms/ProductVariationlInput'
import PurchaseTypeInput, {
  deliveryIntervalDefaultValue,
  deliveryIntervalInputKey,
  purchaseTypeRadioDefaultValue,
  purchaseTypeRadioInputKey,
} from '@/components/Form/Molecules/PurchaseTypeInput'
import { useProcessResponse } from '@/components/hooks/useProcessResponse'
import { useEvent } from '@/components/hooks/useRecordEvent'
import { useZodResolver } from '@/components/hooks/useZodResolver'
import { addCart } from '@/serverActions/addCart'
import { PurchaseType } from '@/types/enum/purchaseType'
import { ProductType } from '@/types/product'

export default function ProductInfoFormLogic({
  product,
  afterSubmit,
  children,
}: {
  product: ProductType
  afterSubmit?: () => void | undefined
  children: ReactNode
}) {
  const { zodDynamicResolver } = useZodResolver()
  const router = useRouter()
  const [PurchaseType, setPurchaseType] = useState<PurchaseType>(purchaseTypeRadioDefaultValue)
  const impressionId = useRecoilValue(impressionIdState)

  const form = useForm({
    mode: 'onSubmit',
    resolver: zodDynamicResolver(
      z.object({
        ...(PurchaseType == 'once' ? {} : deliveryIntervalInputKey.schemaWithName),
        ...productQuantityInputKey.schemaWithName,
        ...purchaseTypeRadioInputKey.schemaWithName,
        ...productVariationScheme([product]),
      })
    ),
    defaultValues: {
      [deliveryIntervalInputKey.name]: deliveryIntervalDefaultValue,
      [productQuantityInputKey.name]: productQuantityDefaultValue,
      [purchaseTypeRadioInputKey.name]: purchaseTypeRadioDefaultValue,
      [productVariationInputKey]: productVariationDefaultValue([product]),
    },
  })
  const { processSuccessResponse, processErrorResponse } = useProcessResponse(form)

  const watch = form.watch(purchaseTypeRadioInputKey.name)
  useEffect(() => setPurchaseType(watch), [watch])
  const { record } = useEvent()

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(async (f) => {
          record('add_cart', {
            productId: product.productId,
            version: 1,
          })

          const response = await addCart({
            productId: f[productVariationInputKey],
            quantity: parseInt(f[productQuantityInputKey.name]),
            purchaseType: f[purchaseTypeRadioInputKey.name] as PurchaseType,
            deliveryInterval: parseInt(f[deliveryIntervalInputKey.name]),
            ...(impressionId ? { impressionId } : {}),
          })

          if (response.success) {
            processSuccessResponse({
              after: () => {
                if (afterSubmit == undefined) {
                  router.push('/carts')
                } else {
                  afterSubmit()
                }
              },
            })
          } else {
            form.reset()
            processErrorResponse(response.errors, 'カートに追加できませんでした。')
          }
        })}
      >
        <PurchaseTypeInput product={product} />

        {children}
      </form>
    </FormProvider>
  )
}
